import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/cn';

export type AlertTone = 'info' | 'success' | 'warning' | 'error';

export interface AlertAction {
  label: string;
  to: string;
}

export interface AlertToastProps {
  title: string;
  description?: string;
  tone?: AlertTone;
  icon?: string;
  action?: AlertAction | null;
  onClose: () => void;
  duration?: number;
}

const TONES: Record<AlertTone, { bar: string; bubble: string; ring: string; fallbackIcon: string }> = {
  info: {
    bar: 'bg-primary-500',
    bubble: 'bg-primary-100 text-primary-700',
    ring: 'border-primary-200',
    fallbackIcon: 'ri-notification-3-line',
  },
  success: {
    bar: 'bg-emerald-500',
    bubble: 'bg-emerald-50 text-emerald-600',
    ring: 'border-emerald-200',
    fallbackIcon: 'ri-checkbox-circle-line',
  },
  warning: {
    bar: 'bg-amber-500',
    bubble: 'bg-amber-100 text-amber-700',
    ring: 'border-amber-200',
    fallbackIcon: 'ri-error-warning-line',
  },
  error: {
    bar: 'bg-rose-500',
    bubble: 'bg-rose-50 text-rose-600',
    ring: 'border-rose-200',
    fallbackIcon: 'ri-error-warning-line',
  },
};

const LEAVE_MS = 200;

/**
 * A single transient attention alert: icon bubble + title + description +
 * optional action link, with a thin countdown bar showing how long it stays.
 * Brand-aware (navy primary), token-based, and direction-aware for RTL.
 */
export default function AlertToast({
  title,
  description,
  tone = 'info',
  icon,
  action,
  onClose,
  duration = 8000,
}: AlertToastProps) {
  const styles = TONES[tone];
  const [leaving, setLeaving] = useState(false);

  const close = useCallback(() => {
    setLeaving(true);
    window.setTimeout(onClose, LEAVE_MS);
  }, [onClose]);

  useEffect(() => {
    const timer = window.setTimeout(close, duration);
    return () => window.clearTimeout(timer);
  }, [close, duration]);

  return (
    <div
      role="status"
      className={cn(
        'lb-alert pointer-events-auto w-full overflow-hidden rounded-lg border lb-glass-strong',
        styles.ring,
        leaving ? 'lb-alert-leave' : 'lb-alert-enter',
      )}
    >
      <div className="flex items-start gap-3 p-3.5">
        <span
          className={cn(
            'flex h-10 w-10 shrink-0 items-center justify-center rounded-md',
            styles.bubble,
          )}
          aria-hidden="true"
        >
          <i className={cn(icon ?? styles.fallbackIcon, 'text-xl leading-none')} />
        </span>

        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold leading-snug text-foreground-950">{title}</p>
          {description ? (
            <p className="mt-1 text-xs leading-relaxed text-foreground-700">{description}</p>
          ) : null}
          {action ? (
            <Link
              to={action.to}
              onClick={close}
              className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-primary-700 transition-colors hover:text-primary-800"
            >
              {action.label}
              <i className="ri-arrow-left-line text-base rtl:rotate-180" aria-hidden="true" />
            </Link>
          ) : null}
        </div>

        <button
          type="button"
          onClick={close}
          aria-label={title}
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-foreground-400 transition-colors hover:bg-background-100 hover:text-foreground-700 cursor-pointer"
        >
          <i className="ri-close-line text-lg" aria-hidden="true" />
        </button>
      </div>

      <span
        className={cn('lb-alert-progress block h-[3px]', styles.bar)}
        style={{ animationDuration: `${duration}ms` }}
        aria-hidden="true"
      />
    </div>
  );
}